import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";

export default function DimensionTable({
  dimension,
  onRowToggle,
  onSelectAll,
  onClear,
  onInvert,
  isLoading = false,
  disabled = false,
  clientColor = "#1976d2",
}) {
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});
  const [rowScope, setRowScope] = useState("all");
  const safeDimension = dimension || {
    column_names: [],
    unique_rows: [],
    selected_rows: [],
  };

  const normalizeCell = (value) =>
    value === null || value === undefined ? "" : String(value).toLowerCase();

  const rowKey = (row) => JSON.stringify(row);

  const activeFilterEntries = useMemo(
    () =>
      Object.entries(columnFilters).filter(([, value]) => value.trim() !== ""),
    [columnFilters],
  );

  const hasTextFilter = activeFilterEntries.length > 0;
  const hasActiveFilter = hasTextFilter || rowScope === "selected";

  const textFilteredRows = useMemo(() => {
    if (!hasTextFilter) {
      return safeDimension.unique_rows;
    }

    return safeDimension.unique_rows.filter((row) =>
      activeFilterEntries.every(([columnIndex, filterValue]) =>
        normalizeCell(row[Number(columnIndex)]).includes(
          filterValue.trim().toLowerCase(),
        ),
      ),
    );
  }, [activeFilterEntries, hasTextFilter, safeDimension.unique_rows]);

  const selectedRowKeys = useMemo(
    () => new Set(safeDimension.selected_rows.map((row) => rowKey(row))),
    [safeDimension.selected_rows],
  );

  const isRowSelected = (row) => selectedRowKeys.has(rowKey(row));

  const filteredRows = useMemo(() => {
    if (rowScope !== "selected") {
      return textFilteredRows;
    }

    return textFilteredRows.filter((row) => selectedRowKeys.has(rowKey(row)));
  }, [rowScope, selectedRowKeys, textFilteredRows]);

  const selectableRows = hasActiveFilter
    ? filteredRows
    : safeDimension.unique_rows;
  const selectedCountInScope = selectableRows.filter(isRowSelected).length;
  const allSelected =
    selectableRows.length > 0 && selectedCountInScope === selectableRows.length;
  const someSelected = selectedCountInScope > 0 && !allSelected;

  const handleOpenFilter = (event, columnIndex) => {
    setActiveFilterColumn(columnIndex);
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
    setActiveFilterColumn(null);
  };

  const handleClearFilter = () => {
    if (activeFilterColumn === null) {
      handleCloseFilter();
      return;
    }

    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[activeFilterColumn];
      return next;
    });
    handleCloseFilter();
  };

  const handleRemoveColumnFilter = (columnIndex) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[columnIndex];
      return next;
    });
  };

  const handleFilterChange = (event) => {
    const value = event.target.value;
    setColumnFilters((prev) => ({
      ...prev,
      [activeFilterColumn]: value,
    }));
  };

  const handleSelectAllClick = () => {
    if (!hasActiveFilter) {
      onSelectAll();
      return;
    }

    filteredRows.forEach((row) => {
      if (!isRowSelected(row)) {
        onRowToggle(row);
      }
    });
  };

  if (!dimension) {
    return null;
  }

  return (
    <Box
      flex={1}
      display="flex"
      flexDirection="column"
      style={{ minHeight: 0, minWidth: 0, height: "100%", paddingLeft: 16 }}
    >
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          maxWidth: "100%",
          overflow: "visible",
          position: "relative",
        }}
      >
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          style={{
            gap: 4,
            position: "absolute",
            top: -28,
            left: 8,
            zIndex: 4,
            maxWidth: "calc(100% - 16px)",
            pointerEvents: "auto",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            style={{
              border: `1px solid ${clientColor}40`,
              borderRadius: "10px 10px 0 0",
              overflow: "hidden",
              backgroundColor: "#fff",
            }}
          >
            {[
              ["all", "All visible"],
              ["selected", "Selected"],
            ].map(([scope, label]) => {
              const isActive = rowScope === scope;
              return (
                <Button
                  key={scope}
                  size="small"
                  onClick={() => setRowScope(scope)}
                  style={{
                    minWidth: 76,
                    height: 22,
                    padding: "0 8px",
                    borderRadius: 0,
                    fontSize: "0.68rem",
                    lineHeight: 1,
                    textTransform: "none",
                    color: isActive ? "#fff" : clientColor,
                    backgroundColor: isActive ? clientColor : "transparent",
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Box>
          {hasTextFilter &&
            activeFilterEntries.map(([columnIndex, filterValue]) => (
              <Chip
                key={columnIndex}
                label={`${dimension.column_names[Number(columnIndex)]}: ${filterValue}`}
                size="small"
                onDelete={() => handleRemoveColumnFilter(columnIndex)}
                style={{
                  height: 22,
                  fontSize: "0.7rem",
                  backgroundColor: `${clientColor}15`,
                  border: `1px solid ${clientColor}40`,
                  color: clientColor,
                }}
              />
            ))}
        </Box>
        <TableContainer
          component={Paper}
          style={{
            width: "fit-content",
            minWidth: 240,
            maxWidth: "100%",
            height: "100%",
            maxHeight: "100%",
            overflowY: disabled ? "hidden" : "auto",
            overflowX: "auto",
            position: "relative",
          }}
        >
          <Table
            stickyHeader
            size="small"
            style={{ width: "max-content", minWidth: 240, maxWidth: "none" }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    onChange={handleSelectAllClick}
                    checked={allSelected}
                    indeterminate={someSelected}
                    disabled={
                      isLoading || disabled || selectableRows.length === 0
                    }
                  />
                </TableCell>
                {dimension.column_names.map((col, columnIndex) => {
                  const isColumnFiltered =
                    (columnFilters[columnIndex] || "").trim() !== "";

                  return (
                    <TableCell
                      key={col}
                      style={{
                        fontWeight: 600,
                        color: disabled ? "#999" : undefined,
                        backgroundColor: disabled ? "#f5f5f5" : undefined,
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <span>{col}</span>
                        <IconButton
                          size="small"
                          onClick={(event) =>
                            handleOpenFilter(event, columnIndex)
                          }
                          disabled={isLoading || disabled}
                          style={{
                            padding: 2,
                            marginLeft: 4,
                            color: isColumnFiltered ? "#1976d2" : "#999",
                          }}
                        >
                          <SearchIcon style={{ fontSize: "0.9rem" }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => (
                <TableRow
                  key={rowKey(row)}
                  hover={!disabled}
                  style={{
                    backgroundColor: disabled ? "#f5f5f5" : undefined,
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isRowSelected(row)}
                      onChange={() => onRowToggle(row)}
                      disabled={isLoading || disabled}
                    />
                  </TableCell>
                  {row.map((cell, cellIdx) => (
                    <TableCell
                      key={cellIdx}
                      style={{
                        color: disabled ? "#999" : undefined,
                      }}
                    >
                      {cell === null || cell === undefined ? "" : String(cell)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box display="flex" alignItems="center" style={{ padding: 8, gap: 4 }}>
          <TextField
            autoFocus
            size="small"
            variant="outlined"
            placeholder="Filter..."
            value={
              activeFilterColumn === null
                ? ""
                : columnFilters[activeFilterColumn] || ""
            }
            onChange={handleFilterChange}
            style={{ width: 180 }}
          />
          <IconButton size="small" onClick={handleClearFilter}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Popover>

      {/* Footer Actions */}
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        marginTop={1}
        style={{ minWidth: 280 }}
      >
        <Button
          size="small"
          onClick={handleSelectAllClick}
          disabled={isLoading || disabled || selectableRows.length === 0}
        >
          Select All
        </Button>
        <Typography style={{ color: "#ccc" }}>|</Typography>
        <Button
          size="small"
          onClick={onClear}
          disabled={isLoading || disabled || hasActiveFilter}
        >
          Clear
        </Button>
        <Typography style={{ color: "#ccc" }}>|</Typography>
        <Button
          size="small"
          onClick={onInvert}
          disabled={isLoading || disabled || hasActiveFilter}
        >
          Invert
        </Button>
        {hasActiveFilter && (
          <Typography
            variant="caption"
            style={{ color: "#888", marginLeft: 8 }}
          >
            {filteredRows.length} of {dimension.unique_rows.length} rows shown
          </Typography>
        )}
      </Box>
    </Box>
  );
}

DimensionTable.propTypes = {
  dimension: PropTypes.shape({
    column_names: PropTypes.arrayOf(PropTypes.string).isRequired,
    unique_rows: PropTypes.arrayOf(PropTypes.array).isRequired,
    selected_rows: PropTypes.arrayOf(PropTypes.array).isRequired,
  }),
  onRowToggle: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onInvert: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  clientColor: PropTypes.string,
};
